/**
 * ╔═══════════════════════════════════════════════════════════════════════════╗
 * ║  STICKY TIMER BAR - Always visible countdown                              ║
 * ║  Sticks to top of viewport when user scrolls past HeroStats               ║
 * ╚═══════════════════════════════════════════════════════════════════════════╝
 */

import { motion, AnimatePresence } from 'framer-motion';
import { formatFRAME } from '../utils/format';
import { shortWallet } from '../utils/drones';

interface StickyTimerProps {
  timeRemaining: number;
  pot: bigint;
  currentPrice: bigint;
  lastBuyer: string;
  /** Set to true when the HeroStats section scrolls out of view */
  visible: boolean;
  onBuyClick?: () => void;
}

type TimerState = 'safe' | 'warning' | 'danger' | 'critical' | 'final' | 'ended';

function getTimerState(seconds: number): TimerState {
  if (seconds < 0) return 'safe';
  if (seconds === 0) return 'ended';
  if (seconds < 60) return 'final';
  if (seconds < 300) return 'critical';
  if (seconds < 1800) return 'danger';
  if (seconds < 3600) return 'warning';
  return 'safe';
}

function formatTime(totalSeconds: number): string {
  if (totalSeconds < 0) return '--:--:--';
  if (totalSeconds === 0) return '00:00:00';
  const h = Math.floor(totalSeconds / 3600);
  const m = Math.floor((totalSeconds % 3600) / 60);
  const s = totalSeconds % 60;
  return `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
}

function truncateAddress(addr: string): string {
  if (!addr || addr === '0x0000000000000000000000000000000000000000') return '...';
  return shortWallet(addr);
}

const STATE_COLORS: Record<TimerState, { bg: string; border: string; text: string; glow: string }> = {
  safe:     { bg: 'rgba(0,255,136,0.05)',  border: 'rgba(0,255,136,0.3)',  text: '#00ff88', glow: '0 0 20px rgba(0,255,136,0.2)' },
  warning:  { bg: 'rgba(255,140,50,0.05)', border: 'rgba(255,140,50,0.3)', text: '#ff8c32', glow: '0 0 20px rgba(255,140,50,0.2)' },
  danger:   { bg: 'rgba(255,102,0,0.08)',  border: 'rgba(255,102,0,0.4)',  text: '#ff6600', glow: '0 0 30px rgba(255,102,0,0.3)' },
  critical: { bg: 'rgba(255,51,0,0.1)',    border: 'rgba(255,51,0,0.5)',   text: '#ff3300', glow: '0 0 40px rgba(255,51,0,0.4)' },
  final:    { bg: 'rgba(255,0,0,0.12)',    border: 'rgba(255,0,0,0.6)',    text: '#ff0000', glow: '0 0 50px rgba(255,0,0,0.5)' },
  ended:    { bg: 'rgba(234,179,8,0.08)',  border: 'rgba(234,179,8,0.4)',  text: '#eab308', glow: '0 0 30px rgba(234,179,8,0.3)' },
};

export default function StickyTimer({ timeRemaining, pot, currentPrice, lastBuyer, visible, onBuyClick }: StickyTimerProps) {
  const state = getTimerState(timeRemaining);
  const colors = STATE_COLORS[state];

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          initial={{ y: -60, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: -60, opacity: 0 }}
          transition={{ duration: 0.25, ease: 'easeOut' }}
          className="fixed top-0 left-0 right-0 z-50"
          style={{
            background: `linear-gradient(180deg, rgba(0,0,0,0.95) 0%, ${colors.bg} 100%)`,
            borderBottom: `1px solid ${colors.border}`,
            boxShadow: `${colors.glow}, 0 4px 20px rgba(0,0,0,0.8)`,
            backdropFilter: 'blur(20px)',
          }}
        >
          <div className="container mx-auto max-w-7xl px-3 py-2">
            <div className="flex items-center justify-between gap-3">
              {/* Timer */}
              <div className="flex items-center gap-2">
                <motion.div
                  className="w-2 h-2 rounded-full"
                  style={{ background: colors.text }}
                  animate={state === 'critical' || state === 'final' ? { opacity: [1, 0.3, 1] } : { opacity: 1 }}
                  transition={{ duration: 0.5, repeat: Infinity }}
                />
                <span
                  className="font-orbitron text-lg sm:text-xl font-bold tracking-wider"
                  style={{ color: colors.text }}
                >
                  {formatTime(timeRemaining)}
                </span>
                {state === 'ended' && (
                  <span className="text-xs text-yellow-400 font-rajdhani animate-pulse">ROUND ENDING</span>
                )}
              </div>

              {/* Pot */}
              <div className="hidden sm:flex items-center gap-2">
                <span className="text-xs text-gray-500 font-rajdhani">POT</span>
                <span className="font-mono text-sm text-green-400 font-bold">{formatFRAME(pot)} FRAME</span>
              </div>

              {/* Key Price */}
              <div className="hidden sm:flex items-center gap-2">
                <span className="text-xs text-gray-500 font-rajdhani">KEY</span>
                <span className="font-mono text-sm text-fuchsia-400 font-bold">{formatFRAME(currentPrice)} FRAME</span>
              </div>

              {/* Last buyer */}
              <div className="hidden md:flex items-center gap-2">
                <span className="text-xs text-gray-500 font-rajdhani">LEADER</span>
                <span className="font-mono text-xs text-neon-cyan">{truncateAddress(lastBuyer)}</span>
              </div>

              {/* Buy button */}
              <motion.button
                onClick={onBuyClick}
                className="px-4 py-1.5 rounded-lg font-orbitron text-xs font-bold uppercase tracking-wider btn-buy"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                BUY KEYS
              </motion.button>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
